import { useEffect, useState } from "react";
import { campaignApi } from "../api/campaign";
import { useCampaignRunStore } from "../store/useCampaignRunStore";
import { useGameStore } from "../store/useGameStore";
import type { CampaignStage } from "../types";
import clsx from "clsx";
import { energyCostLabel, hasEnergyFor } from "../utils/assets";
import type { Hero } from "../types";

interface TierGroup {
  tier: number;
  stages: CampaignStage[];
}

function groupByTier(stages: CampaignStage[]): TierGroup[] {
  const map = new Map<number, CampaignStage[]>();
  for (const s of stages) {
    if (!map.has(s.tier)) map.set(s.tier, []);
    map.get(s.tier)!.push(s);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([tier, list]) => ({ tier, stages: list.sort((a, b) => a.stage_number - b.stage_number) }));
}

export function CampaignScreen() {
  const [stages, setStages] = useState<CampaignStage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { enterStage, isRunLocked, syncActiveRun } = useCampaignRunStore();
  const { hero, setScreen } = useGameStore();

  useEffect(() => {
    void syncActiveRun().then((locked) => {
      if (locked) setScreen("campaignRun");
    });
  }, [setScreen, syncActiveRun]);

  useEffect(() => {
    let cancelled = false;
    campaignApi
      .getStages()
      .then((data) => !cancelled && setStages(data))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "Failed to load campaign"));
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePlay = async (stage: CampaignStage) => {
    if (!stage.unlocked || isRunLocked) return;
    const ok = await enterStage(stage);
    if (ok) setScreen("campaignRun");
  };

  if (isRunLocked) {
    return (
      <div className="p-4 text-center text-gray-400 animate-pulse py-12">
        Returning to active campaign...
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-xl font-bold font-game text-center text-gold-400">📖 Campaign</h2>
      <p className="text-gray-400 text-center text-sm">
        8 stages per chapter with branching paths inside. Entry costs{" "}
        {energyCostLabel(hero, "CAMPAIGN") || "⚡ 15"} · node fights are free.
      </p>

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}

      {!stages && !error && (
        <div className="text-center py-12 text-gray-400 animate-pulse">Loading campaign...</div>
      )}

      {stages && (
        <div className="space-y-5">
          {groupByTier(stages).map((group) => (
            <TierSection
              key={group.tier}
              group={group}
              heroLevel={hero?.level ?? 1}
              hero={hero}
              onPlay={handlePlay}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TierSection({
  group,
  heroLevel,
  hero,
  onPlay,
}: {
  group: TierGroup;
  heroLevel: number;
  hero: Hero | null;
  onPlay: (stage: CampaignStage) => void;
}) {
  const theme = group.stages[0]?.name.split(" — ")[0] ?? `Tier ${group.tier + 1}`;
  const minLevel = group.stages[0]?.min_hero_level ?? 0;
  const clearedCount = group.stages.filter((s) => s.completed).length;

  return (
    <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-white">
            Chapter {group.tier + 1}: {theme}
          </div>
          <div className="text-[11px] text-gray-500">Recommended level {minLevel}+</div>
        </div>
        <span className="text-xs text-kingdom-glow font-mono">
          {clearedCount}/{group.stages.length}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {group.stages.map((stage) => (
          <StageTile key={stage.id} stage={stage} heroLevel={heroLevel} hero={hero} onPlay={onPlay} />
        ))}
      </div>
    </div>
  );
}

function StageTile({
  stage,
  heroLevel,
  hero,
  onPlay,
}: {
  stage: CampaignStage;
  heroLevel: number;
  hero: Hero | null;
  onPlay: (stage: CampaignStage) => void;
}) {
  const locked = !stage.unlocked;
  const underleveled = heroLevel < stage.min_hero_level;
  const noEnergy = !hasEnergyFor(hero, "CAMPAIGN");

  return (
    <button
      onClick={() => onPlay(stage)}
      disabled={locked || noEnergy}
      className={clsx(
        "relative flex flex-col items-center justify-center rounded-lg p-2 aspect-square border-2 transition-all active:scale-95",
        locked && "bg-gray-900 border-gray-700 opacity-50 cursor-not-allowed",
        !locked && noEnergy && "opacity-50 cursor-not-allowed",
        !locked && stage.completed && "bg-emerald-950 border-emerald-600",
        !locked && !stage.completed && stage.is_boss && "bg-rose-950 border-rose-500",
        !locked && !stage.completed && !stage.is_boss && "bg-kingdom-mid border-kingdom-light hover:border-kingdom-glow",
      )}
      title={stage.name}
    >
      <span className="text-xl">
        {locked ? "🔒" : stage.is_boss ? "👑" : stage.completed ? "✅" : "⚔️"}
      </span>
      <span className="text-[9px] text-gray-300 mt-0.5">
        {stage.is_boss ? "Boss" : `#${stage.stage_number}`}
      </span>
      {!locked && underleveled && (
        <span className="absolute -top-1 -right-1 text-[8px] bg-amber-500 text-black rounded-full px-1 font-bold">
          !
        </span>
      )}
    </button>
  );
}
